import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum, type RumPlugin } from '@datadog/browser-rum';
import type { AuthProviderContext, AuthProviderProps } from '@newfront-insurance/next-auth';
import type { Provider } from '@newfront-insurance/react-provision';
import { useProvider } from '@newfront-insurance/react-provision';
import type { ConfigType } from '@newfront-insurance/shared-public-config';
import { getConfig, getEnvFromUrl } from '@newfront-insurance/shared-public-config';
import { useEffect, type JSX } from 'react';

import { ABORT_ERROR_MESSAGES } from './abort-error';

// eslint-disable-next-line no-process-env
const isRumEnabled = typeof window !== 'undefined' && process.env.NODE_ENV === 'production';

const ERRORS_TO_IGNORE: (string | RegExp)[] = [
  'ResizeObserver',
  'Abort fetching component for route',
  'Abort route change by user',
  'Route did not complete loading',
  'Failed to lookup route',
  '[LaunchDarkly]',
  'Failed to load script',
  'Failed to fetch',
  'Network Error',
  // Benign request cancellations (AbortError). Single source of truth in abort-error.ts
  // so isAbortError() and the RUM/logs filters can't drift.
  ...ABORT_ERROR_MESSAGES,
  'Uncaught "Script error."',
  /Uncaught "Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+"/,
  '"undefined" is not valid JSON',
  'Invalid call to runtime.sendMessage().',
];

function matchesIgnoredError(message: string): boolean {
  return ERRORS_TO_IGNORE.some((errorToIgnore) =>
    errorToIgnore instanceof RegExp ? errorToIgnore.test(message) : message.includes(errorToIgnore),
  );
}

const rumAppNames: Record<keyof ConfigType['DATADOG']['RUM'], string> = {
  ACCOUNTS: 'account-app',
  BULK_IMPORT: 'bulk-import-app',
  CLAIMS: 'claims-app',
  CLIENT_DASH: 'client-dash-app',
  CONVERSION: 'conversion-app',
  FILES: 'files-app',
  NOVA: 'nova-app',
  NOVA_ADDINS: 'nova-addins-app',
  PAYMENTS: 'payments-app',
  PROOF_OF_INSURANCE: 'proof-of-insurance-app',
  PROPOSAL: 'proposal-app',
  QA_ANALYTICS: 'qa-analytics-app',
  QUOTING: 'quoting-app',
  TRADING_PARTNERS: 'trading-partners-app',
};

interface InitializeDatadogLogsAndRUMOptions {
  rumPlugins?: RumPlugin[];
}

export function initializeDatadogLogsAndRUM(
  appName: keyof ConfigType['DATADOG']['RUM'],
  options: InitializeDatadogLogsAndRUMOptions = {},
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const env = getEnvFromUrl();
  const config = getConfig();
  const rumConfig = config.DATADOG.RUM[appName];
  const rumAppName = rumAppNames[appName];

  // initializes both logs and rum
  if (isRumEnabled) {
    datadogLogs.init({
      clientToken: rumConfig.CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: rumAppName,

      env,
      enableExperimentalFeatures: ['feature_flags'],
      // eslint-disable-next-line no-process-env
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      trackAnonymousUser: true,
      allowedTrackingOrigins: [window.location.origin],
      forwardErrorsToLogs: true,
      forwardReports: 'all',
      forwardConsoleLogs: 'all',
      beforeSend(log) {
        // Mirror the RUM error filter on the logs pipeline. Without this,
        // browserLogger.error(...) output reaches Datadog completely unfiltered,
        // so benign cancellations (AbortError) and other ignored errors are
        // logged. We check both the log message and any forwarded error message.
        const messages = [log.message, log.error?.message].filter(
          (value): value is string => typeof value === 'string',
        );
        if (messages.some(matchesIgnoredError)) {
          return false;
        }
        return true;
      },
    });
    datadogLogs.logger.setLevel('info');

    datadogRum.init({
      applicationId: rumConfig.APPLICATION_ID,
      clientToken: rumConfig.CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: rumAppName,
      env,
      allowedTracingUrls: [window.location.origin, config.API.GRAPHQL_BFF, config.API.AI],
      // eslint-disable-next-line no-process-env
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      allowedGraphQlUrls: [config.API.GRAPHQL_BFF],
      ...(options.rumPlugins ? { plugins: options.rumPlugins } : {}),
      traceSampleRate: 100,
      defaultPrivacyLevel: 'allow',
      beforeSend(event) {
        if (event.type === 'error' && matchesIgnoredError(event.error.message)) {
          return false;
        }

        return true;
      },
    });

    datadogRum.startSessionReplayRecording();
  }
}

interface UserData {
  uuid: string;
  email: string;
  firstName: string;
  lastName: string;
}

function setDatadogSessionUser(user?: UserData): void {
  if (user) {
    const userData = {
      email: user.email,
      id: user.uuid,
      name: `${user.firstName} ${user.lastName}`,
    };
    // Example of how to log with datadog
    browserLogger.info('Setting datadog user', userData);
    datadogRum.setUser(userData);
    datadogLogs.setUser(userData);
  }
}

interface Props {
  authProvider: Provider<AuthProviderContext, AuthProviderProps>;
  children: JSX.Element;
}

export function DatadogRumWrapper({ authProvider, children }: Props) {
  const { userDetails } = useProvider(authProvider);

  useEffect(() => {
    if (userDetails && isRumEnabled) {
      setDatadogSessionUser(userDetails);
    }
  }, [userDetails]);

  return children;
}

interface ViewWrapperProps {
  viewName?: string;
  service: string;
  children: JSX.Element;
}

/**
 * Used to apply a custom name and service to a view
 */
export function DataDogViewWrapper({ viewName, service, children }: ViewWrapperProps): JSX.Element {
  useEffect(() => {
    if (viewName) {
      datadogRum.startView({ name: viewName, service });
    } else {
      datadogRum.startView({ service });
    }
  }, [viewName, service]);
  return children;
}

// If we are not in prod, we want to log to the console instead of datadog
const browserLogger = isRumEnabled
  ? datadogLogs.createLogger('browser', {
      level: 'info',
      context: {
        env: getEnvFromUrl(),
      },
    })
  : console;

export { datadogRum, browserLogger };
export { isAbortError } from './abort-error';
